import { useState, type CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { HaiIcon } from '@/components/icons';
import { useCheckBatchListener } from '@/features/assets/check/useCheckBatchListener';
import { services } from '@/services';
import { useCheckBatchStore } from '@/stores/checkBatchStore';
import { checkBatchPct } from '@/types/check';

/**
 * CheckBatchMinibar — 跨頁批次檢查常駐條(R209;D3 check-shell.js paintMinibar)。
 * -----------------------------------------------------------------------------
 * 右下角固定;三態:檢查中(n/N + % + 展開)/ 完成(N 項完成 · M 項待調整 +
 * 查看結果)/ 失敗(n 完成 · m 失敗 + 查看結果);可關閉(新批次重置)。
 * 掛 AppShell(全站頁面都經 AppShell → 天然跨頁;SPA 內 store 常駐,不需
 * storage 事件 — 跨 tab 同步屬 P2)。
 * **與素材庫頁互斥**:pathname /assets 開頭時一律不顯(頁內進度條 + 清單 badge
 * 已呈現;mock 素材庫頁 paintMinibar 即空 stub)。
 * 三禁(panel CR;AppShell 被 16 頁引用,違者全站 page test 連坐):
 * 禁 useToast、無 batch 不 render、mount 期不打 service(訂閱 glue 只在
 * store 有 run 中 batch 才動)。
 *
 * R212 對帳 docs/88 §2.1:
 *   - **失敗態不再把 `done` 扣掉 `failed`** —— wire 的 `done`(成功件)與 `failed`
 *     是分開計的兩個計數(§2.1),相減等於把失敗件數扣兩次(3 成功 / 2 失敗 會顯
 *     成「1 項完成 · 2 項失敗」)。
 *   - **待調整件數吃 wire `counts`**(fail + warn),與逐件 results 取大值。
 *   - `listenerBlocked` 降級態給手動「重新整理進度」出口(icon-only,省寬)。
 *     失敗**不發 toast**(三禁第一條:AppShell 沒有 ToastProvider,`useToast` 會
 *     throw → 16 頁 page test 連坐)→ 就地顯一行「更新失敗」。
 */

/**
 * minibar 此刻在不在畫面上 —— 右下角其他浮層(`BackToTopButton`)靠它避讓。
 * 與元件本體共用同一份判準:兩處各寫一次,改了其中一個就會疊在一起。
 * 純讀 store + location,**不**掛 listener(那是 minibar 自己的事)。
 */
export function useCheckMinibarVisible(): boolean {
  const { pathname } = useLocation();
  const batch = useCheckBatchStore((s) => s.batch);
  const minibarClosed = useCheckBatchStore((s) => s.minibarClosed);

  if (!batch || minibarClosed) return false;
  if (batch.state === 'cancelled') return false;
  if (pathname.startsWith('/assets')) return false;
  return true;
}

export function CheckBatchMinibar() {
  useCheckBatchListener();
  const { t } = useTranslation('check');
  const navigate = useNavigate();
  const visible = useCheckMinibarVisible();
  const batch = useCheckBatchStore((s) => s.batch);
  const closeMinibar = useCheckBatchStore((s) => s.closeMinibar);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);

  // `!batch` 是 visible 的子條件,列出來只為了讓 TS narrow 掉下面的 batch.*
  if (!visible || !batch) return null;

  const goAssets = () => navigate('/assets?check=1');

  const onRefresh = () => {
    if (refreshBusy) return;
    setRefreshBusy(true);
    setRefreshFailed(false);
    void services.checkService
      .getBatch(batch.batchId)
      .then((next) => {
        const store = useCheckBatchStore.getState();
        if (!next) {
          store.clearBatch();
          return;
        }
        // 同一批走 patch:保住 FE-only 的 `listenerBlocked`(REST 快照沒這欄,整包
        // 覆蓋會讓手動出口自己消失)
        if (store.batch?.batchId === next.batchId) store.patchBatch(next);
        else store.setBatch(next);
      })
      .catch(() => setRefreshFailed(true))
      .finally(() => setRefreshBusy(false));
  };

  let body: React.ReactNode;
  if (batch.state === 'run') {
    body = (
      <>
        <span style={spinStyle} aria-hidden />
        <span style={titleStyle}>{t('minibar.run', { done: batch.done, total: batch.total })}</span>
        <span style={pctStyle}>{checkBatchPct(batch)}%</span>
        <span style={{ flex: 1 }} />
        {batch.listenerBlocked ? (
          <>
            {refreshFailed ? (
              <span style={refreshFailedStyle} data-testid="check-minibar-refresh-failed">
                {t('minibar.refreshFailed')}
              </span>
            ) : null}
            <button
              type="button"
              style={refreshBusy ? iconBtnBusyStyle : iconBtnStyle}
              onClick={onRefresh}
              disabled={refreshBusy}
              title={t('minibar.refresh')}
              aria-label={t('minibar.refresh')}
              data-testid="check-minibar-refresh"
            >
              <HaiIcon name="redo" size={11} />
            </button>
          </>
        ) : null}
        <button type="button" style={linkBtnStyle} onClick={goAssets} title={t('minibar.expandTitle')} data-testid="check-minibar-expand">
          {t('minibar.expand')}
        </button>
      </>
    );
  } else {
    // 待調整 = 可能退件 + 建議調整;wire counts 與逐件 results 取大值(兩路推播先後
    // 不保證,fixture 佔位實作只維護 results)
    const derivedWarn = Object.values(batch.results).filter((b) => b.s === 'warn' || b.s === 'fail').length;
    const warn = Math.max((batch.counts?.fail ?? 0) + (batch.counts?.warn ?? 0), derivedWarn);
    const label =
      batch.state === 'fail'
        ? t('minibar.fail', { done: batch.done, failed: batch.failed })
        : t('minibar.done', { total: batch.total, warn });
    body = (
      <>
        <span style={batch.state === 'fail' ? warnIcStyle : okIcStyle} aria-hidden>
          {batch.state === 'fail' ? <HaiIcon name="warn" size={11} /> : <HaiIcon name="ck" size={11} />}
        </span>
        <span style={titleStyle}>{label}</span>
        <span style={{ flex: 1 }} />
        <button type="button" style={linkBtnStyle} onClick={goAssets} data-testid="check-minibar-view">
          {t('minibar.view')}
        </button>
      </>
    );
  }

  return (
    <div style={barStyle} role="status" data-testid="check-minibar">
      {body}
      <button
        type="button"
        style={closeBtnStyle}
        onClick={closeMinibar}
        title={t('minibar.close')}
        aria-label={t('minibar.close')}
        data-testid="check-minibar-close"
      >
        <HaiIcon name="x" size={11} />
      </button>
    </div>
  );
}

// ─── styles(check-shell #minibar;素材庫.html:466-479)──────────────────────

const barStyle: CSSProperties = {
  position: 'fixed',
  right: 20,
  bottom: 20,
  zIndex: 100,
  display: 'flex',
  alignItems: 'center',
  gap: 11,
  /* R218 浮層層級:深色比卡片再亮一階(D3「外框最暗 → 內容 → 浮層最亮」);淺色同白 */
  background: 'var(--surface-pop)',
  border: '1px solid var(--border-pop)',
  borderRadius: 12,
  boxShadow: 'var(--shadow-lg)',
  padding: '9px 11px 9px 13px',
  minWidth: 268,
};

const titleStyle: CSSProperties = {
  fontSize: 12.5,
  fontWeight: 700,
  color: 'var(--text-strong)',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

const pctStyle: CSSProperties = {
  fontSize: 11.5,
  color: 'var(--ink-500)',
  fontVariantNumeric: 'tabular-nums',
};

/** 「更新失敗」= 失敗訊息 → CIS 錯誤色 rose(字走 700 階,深色主題會翻亮)。 */
const refreshFailedStyle: CSSProperties = {
  fontSize: 11.5,
  color: 'var(--rose-700)',
};

const spinStyle: CSSProperties = {
  width: 14,
  height: 14,
  borderRadius: '50%',
  border: '2px solid var(--tide-200)',
  borderTopColor: 'var(--tide-600)',
  animation: 'hai-chk-spin 0.7s linear infinite',
  flex: '0 0 14px',
};

const okIcStyle: CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: 9999,
  color: 'var(--on-accent)',
  background: 'var(--tide-600)',
  display: 'grid',
  placeItems: 'center',
  flex: '0 0 18px',
};

/**
 * 失敗態圖示 —— **淡底深字**,不沿用 okIcStyle 的實心 + `--on-accent`。
 * `--amber-700` 深色反轉成 #FCD34D(亮黃)而 `--on-accent` 恆白 → 實心寫法在深色下
 * 是 1.44:1,成功/失敗兩態會變成看起來一樣的亮色圓點(這是狀態訊號,不能糊掉)。
 * teal 那顆沒這問題(深色 #0F766E 配白字 5.47:1),所以只有這顆改。
 */
const warnIcStyle: CSSProperties = {
  ...okIcStyle,
  background: 'var(--amber-50)',
  color: 'var(--amber-700)',
};

const linkBtnStyle: CSSProperties = {
  fontSize: 11.5,
  fontWeight: 700,
  color: 'var(--concept-fg)',
  padding: '4px 7px',
  borderRadius: 7,
  whiteSpace: 'nowrap',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
};

const closeBtnStyle: CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: 8,
  border: 'none',
  background: 'none',
  color: 'var(--ink-600)',
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
  flex: '0 0 26px',
};

/** 手動更新進度(只在 listener 被擋的降級態出現;icon-only 以免撐寬固定條)。 */
const iconBtnStyle: CSSProperties = {
  ...closeBtnStyle,
  color: 'var(--concept-fg)',
};

/** 更新中(disabled)— CIS 停用態走 `--btn-disabled-fg`,不整顆 opacity 調淡。 */
const iconBtnBusyStyle: CSSProperties = {
  ...iconBtnStyle,
  color: 'var(--btn-disabled-fg)',
  cursor: 'not-allowed',
};
